import { Link } from '@arp/react-ndbx';
import { Typography } from '@mui/material';
import { LocalLink } from 'src/components/coding/link';
import RecordOfAnswers from 'src/components/pageLevel/policyOutcome/RecordOfAnswers';
import { URLS } from 'src/config/const/enums';
import useSharedData from 'src/hooks/useSharedData';
import { PATH_ONLINE } from 'src/routes/paths';
import { formatDate } from 'src/utilities/mappingHelper';

export const yourDetailsContactDisclaimers = {
  PRIVACY_NOTICE_CONTACT_DISCLAIMER: {
    note: (
      <>
        Please read the{' '}
        <Link href={URLS.YOUR_DUTY_EXCLUSION_PRIVACY_NOTICE_URL} target="_blank">
          Privacy notice
        </Link>
        which sets out how Allianz handles its life insurance customer’s personal details.
      </>
    ),
    pre_approved_consent_note: (
      <>
        By providing your contact details and voluntarily clicking on the NEXT button at the bottom
        of this page, you voluntarily consent to us contacting you (by phone, email, or SMS) in
        relation to this quote and application for an Allianz Life insurance policy. Your consent
        shall remain in effect in accordance with relevant law or until you tell us otherwise. You
        can vary or withdraw your consent at any time by contacting us on{' '}
        <LocalLink variant="p3-link-bold" href={URLS.CONSENT_PHONE_URL} sx={{ fontSize: 16 }}>
          {URLS.CONSENT_PHONE_NUMBER}
        </LocalLink>
        . Please read the
        <Link
          href={URLS.YOUR_DUTY_EXCLUSION_PRIVACY_NOTICE_URL}
          target="_blank"
          sx={{ fontSize: 16 }}
        >
          Privacy notice
        </Link>
        which sets out how Allianz handles its life insurance customer’s personal details.
      </>
    ),
  },
};

export const saveAndEmailDisclaimers = {
  PRIVACY_NOTICE_CONTACT_DISCLAIMER: {
    note: (
      <>
        Please read the{' '}
        <Link href={URLS.YOUR_DUTY_EXCLUSION_PRIVACY_NOTICE_URL} target="_blank">
          Privacy notice
        </Link>
        which sets out how Allianz handles its life insurance customer’s personal details.
      </>
    ),
    pre_approved_consent_note: (
      <>
        By providing your contact details and voluntarily clicking on the SAVE AND EMAIL QUOTE or
        the START APPLICATION button(s) at the bottom of this page, you voluntarily consent to us
        contacting you (by phone, email, or SMS) in relation to this quote and application for an
        Allianz Life insurance policy. Your consent shall remain in effect in accordance with
        relevant law or until you tell us otherwise. You can vary or withdraw your consent at any
        time by contacting us on{' '}
        <LocalLink variant="p3-link-bold" href={URLS.CONSENT_PHONE_URL} sx={{ fontSize: 16 }}>
          {URLS.CONSENT_PHONE_NUMBER}
        </LocalLink>
        . Please read the
        <Link
          href={URLS.YOUR_DUTY_EXCLUSION_PRIVACY_NOTICE_URL}
          target="_blank"
          sx={{ fontSize: 16 }}
        >
          Privacy notice
        </Link>
        which sets out how Allianz handles its life insurance customer’s personal details.
      </>
    ),
  },
};

export const selectCoverDisclaimers = () => {
  const { pdsUrl } = useSharedData();
  return {
    OPTIONAL_COVER_DISCLAIMER: {
      title: 'Optional Covers: ',
      note: (
        <>
          Payment of a benefit under one type of cover reduces the Cover Amounts of all remaining
          benefits. In certain circumstances this may mean the end of a cover or the policy.
          Additionally, the <strong>Life cover Buy Back</strong> feature allows you to reinstate the
          reduced Life Cover Amount 12 months after full payment of the first eligible Critical
          Illness or Permanently Unable to Work claim. Refer to the
          <Link href={pdsUrl} target="_blank">
            Product Disclosure Statement
          </Link>{' '}
          'Taking a combination of cover’ and ‘Maximum benefits payable’ sections for more details
          on limits on Cover Amounts and the ‘Life cover Buy Back’ section.
        </>
      ),
    },
    OPTIONAL_COVER_DISCLAIMER_CONTACT_CENTRE: {
      title: 'Optional Covers: ',
      note: (
        <>
          <br />
          <br />
          Please be aware payment of a benefit under one type of cover reduces the Cover Amount of
          any remaining benefits.
          <br />
          <br />
          For example:
          <br />
          <br />
          A Critical Illness payment would reduce the Cover Amounts on both the Permanently Unable
          to Work and Life benefits by the amount of the Critical Illness payment. <br />
          <br />
          If you had selected the same Cover Amount for each benefit, no cover would remain on your
          policy as a result of the Critical Illness Payment.
          <br />
          <br />
          In addition, your policy provides you with a Life cover Buy Back feature, so if a Critical
          Illness or Permanently Unable to Work claim is paid in full, your Life Cover Amount would
          be reinstated 12 months after we accept your claim.
          <br />
          <br />
          We would notify you 30 days prior to reinstating your Life cover giving you the option to
          opt out of receiving this additional cover. Life cover Buy Back can only be activated
          once.
          <br />
          <br />
          Refer to the Product Disclosure Statement for more details on limits on Cover Amounts.
          <br />
          <br />
          <b>Would you like me to give you a specific example?</b>
          <br />
          <br />
          {'<'} If Yes - Use the example from the PDS.{'>'}
        </>
      ),
    },
    PRIVACY_NOTICE_DISCLAIMER: {
      title: 'By ticking the box below you state to us all of the following:',
      note: (
        <>
          You have read, understand and agree to the terms of our
          <Link href={URLS.YOUR_DUTY_EXCLUSION_PRIVACY_NOTICE_URL} target="_blank">
            Privacy Notice
          </Link>
          . This includes Allianz and our related companies, agents and business partners using your
          personal information to contact you and provide information, documents and notices in
          relation to our or their products or services, by your email and/or phone number. Your
          consent continues until you opt out.
        </>
      ),
    },
    MISREPRESENTATION_DISCLAIMER: {
      note: (
        <>
          {' '}
          You understand that it is important you do not make a misrepresentation. Remember{' '}
          <Link href={URLS.YOUR_DUTY_EXCLUSION_PRIVACY_NOTICE_URL} target="_blank">
            your duty
          </Link>{' '}
          to take reasonable care not to make a misrepresentation.
        </>
      ),
      sunNote: (
        <>
          {' '}
          You understand that when you apply for Allianz Life Plan we provide you with factual
          information only about this life insurance cover. We don't provide any general or personal
          advice that's intended to influence you in deciding about this product or life insurance
          in general. We don't take into account your personal circumstances including: your
          objectives, financial situation or needs, or any information relating to your
          circumstances that we may hold or you may provide us. If you're unsure, seek advice from a
          Financial Adviser before deciding on appropriate insurance cover.
        </>
      ),
    },
    PRODUCT_DISCLOSURE_DISCLAIMER: {
      note: (
        <>
          {' '}
          You have read and understand the{' '}
          <Link href={pdsUrl} target="_blank">
            Allianz Life Plan Product Disclosure Statement and Policy Document
          </Link>{' '}
          which includes important terms and conditions including, for example,
          <Link href={URLS.YOUR_DUTY_EXCLUSION_PRIVACY_NOTICE_URL} target="_blank">
            policy exclusions, exclusion periods and waiting periods
          </Link>
          . The PDS and Target Market Determination for this product are available online at
          <Link href={URLS.ALLIANZ_WEBSITE_URL} target="_blank">
            Allianz Insurance website
          </Link>
          or calling <Link href={URLS.CONTACT_US_PHONE_URL}>{URLS.CONTACT_US_PHONE_NUMBER}</Link>.
        </>
      ),
    },
    TERMS_AND_CONDITIONS: {
      note: 'Applicant 1',
    },
  };
};

export const quoteSummaryDisclaimers = {
  PROVISIONAL_QUOTE_PREMIUM_PROJECTION: {
    title: `Provisional Quote Premium Projection`,
    titleCC: `Application Outcome Premium Projection`,
    note: (
      <>
        This projection is an estimate only, current as of today. This may not be your final
        premium. You must make your own decision or seek your own independent financial advice as to
        whether the cover and amounts are right for you.
        <br /> <br />
        Further details are outlined at the bottom of the table.
      </>
    ),
  },
};

export const QuoteSummaryProceedDisclaimers = () => {
  const { pdsUrl } = useSharedData();
  return {
    PROVISIONAL_QUOTE_PROCEED: {
      title: `By Proceeding you state to us all of the following:`,
      note: (
        <>
          You understand that when you apply for Allianz Life Plan we provide you with factual
          information only about this life insurance cover. We don't provide any general or personal
          advice that's intended to influence you in deciding about this product or life insurance
          in general. We don't take into account your personal circumstances including: your
          objectives, financial situation or needs, or any information relating to your
          circumstances that we may hold or you may provide us. If you're unsure, seek advice from a
          Financial Adviser before deciding on appropriate insurance cover.
          <br /> <br />
          You have read and understand the&nbsp;
          <LocalLink href={pdsUrl} target="_blank" removeSpaces={true}>
            Allianz Life Plan Product Disclosure Statement and Policy Document
          </LocalLink>
          &nbsp; which includes important terms and conditions including, for example,&nbsp;
          <LocalLink href={URLS.POLICY_EXCLUSION} target="_blank" removeSpaces={true}>
            policy exclusions, exclusion periods and waiting periods.
          </LocalLink>
          &nbsp;The PDS and Target Market Determination for this product are available online
          at&nbsp;
          <LocalLink href={URLS.ALLIANZ_WEBSITE_URL} target="_blank" removeSpaces={true}>
            Allianz Insurance website
          </LocalLink>
          &nbsp; or calling&nbsp;
          <LocalLink removeSpaces={true} href={URLS.CONTACT_US_PHONE_URL}>
            {URLS.CONTACT_US_PHONE_NUMBER}
          </LocalLink>
          .
        </>
      ),
    },
  };
};

export const quoteSummaryDynamicDisclaimers = ({ projectionYearlyRate = 5, upperAge = 65 }) => {
  const disclaimerInfo = {
    BELOW_SIXTY_FIVE_ONLINE: {
      note: (
        <>
          Premium Projection Assumptions: This is a premium projection based on the provisional
          quote information and is current as of today’s date, and assumes that you purchased cover
          today based on our standard premium rates for your age, smoking status, and gender, and
          preset as professional/white collar occupation for Permanently Unable to Work Cover (where
          applicable). The actual premium applicable may be higher when we assess your individual
          circumstances. Premium rates and policy fees aren't guaranteed and may change from time to
          time. Any change will apply from the next Policy Anniversary and be advised in the annual
          notice at least 30 days before the new premium rates and/or policy fees apply.
          <br />
          <br />
          Your premium will increase on each Policy Anniversary based on your increase in age and on
          any increase in the Cover Amount. For the purposes of this projection, Cover Amounts are
          based on an assumed Inflation Proofing Increase of {projectionYearlyRate}% (rounded to the
          nearest dollar) automatically applying on each Policy Anniversary in years 1 to 5. If you
          elect not to accept an Inflation Proofing Increase in any given year or years, the actual
          Cover Amount and premium outcome will differ from this projection. The actual Inflation
          Proofing Increase each year will be the greater of {projectionYearlyRate}% or the 12-month
          change in the Consumer Price Index as published each quarter by the Australian Bureau of
          Statistics and will be advised in the Policy Anniversary notice. Any eligible first year
          discount is excluded from the premium projection as it does not extend to subsequent
          Policy Anniversaries.
        </>
      ),
    },
    WITHIN_SIXTY_FIVE_ONLINE: {
      note: (
        <>
          This is a premium projection based on the provisional quote information and is current as
          of today’s date, and assumes that you purchased cover today based on our standard premium
          rates for your age, smoking status, and gender. The actual premium applicable may be
          higher when we assess your individual circumstances. Premium rates and policy fees aren't
          guaranteed and may change from time to time. Any change will apply from the next Policy
          Anniversary and be advised in the annual notice at least 30 days before the new premium
          rates and/or policy fees apply.
          <br />
          <br />
          Your premium will increase on each Policy Anniversary based on your increase in age and on
          any increase in the Cover Amount. For the purposes of this projection, Cover Amounts are
          based on an assumed Inflation Proofing Increase of {projectionYearlyRate}% (rounded to the
          nearest dollar) automatically applying on each Policy Anniversary up until your age is{' '}
          {upperAge}. If you elect not to accept an Inflation Proofing Increase in any given year or
          years, the actual Cover Amount and premium outcome will differ from this projection. The
          actual Inflation Proofing Increase each year will be the greater of {projectionYearlyRate}
          % or the 12-month change in the Consumer Price Index as published each quarter by the
          Australian Bureau of Statistics and will be advised in the Policy Anniversary notice.
          Note: we won't apply any more Inflation Proofing Increases from the Policy Anniversary
          after your {upperAge}th birthday. Any eligible first year discount is excluded from the
          premium projection as it does not extend to subsequent Policy Anniversaries.
        </>
      ),
    },
    OVER_SIXTY_FIVE_ONLINE: {
      note: (
        <>
          This is a premium projection based on the provisional quote information and is current as
          of today’s date, and assumes that you purchased cover today based on our standard premium
          rates for your age, smoking status, and gender.The actual premium applicable may be higher
          when we assess your individual circumstances. Premium rates and policy fees aren’t
          guaranteed and may change from time to time. Any change will apply from the next Policy
          Anniversary and be advised in the annual notice at least 30 days before the new premium
          rates and/or policy fees apply.
          <br />
          <br />
          Premium will increase on each Policy Anniversary based on your increase in age. Any
          eligible first year discount is excluded from the premium projection as it does not extend
          to subsequent Policy Anniversaries.
        </>
      ),
    },
  };
  return { disclaimerInfo };
};

export const outcomeDynamicDisclaimers = ({ projectionYearlyRate = 5, upperAge = 65 }) => {
  const disclaimerInfo = {
    WITHIN_PROJECTED_AGE_RANGE_CONTACT_CENTER: {
      note: (
        <>
          This is a premium projection based on your offer of cover and any terms accepted. It's
          current as of today’s date and assumes that you purchase cover today. Premium rates and
          policy fees aren't guaranteed and may change from time to time. Any change will apply from
          the next Policy Anniversary and be advised in the annual notice at least 30 days before
          the new premium rates and/or policy fees apply.
          <br />
          <br />
          Your premium will increase on each Policy Anniversary based on your increase in age and on
          any increase in the Cover Amount. For the purposes of this projection, Cover Amounts are
          based on an assumed Inflation Proofing Increase of {projectionYearlyRate}% (rounded to the
          nearest dollar) automatically applying on each Policy Anniversary. If you elect not to
          accept an Inflation Proofing Increase in any given year or years, the actual Cover Amount
          and premium outcome will differ from this projection. The actual Inflation Proofing
          Increase each year will be the greater of {projectionYearlyRate}% or the 12-month change
          in the Consumer Price Index as published each quarter by the Australian Bureau of
          Statistics and will be advised in the Policy Anniversary notice. Note: we won't apply any
          more Inflation Proofing Increases from the Policy Anniversary after your {upperAge}th
          birthday.
          <br />
          <br />
          You must make your own decision as to whether the cover and amounts are right for you.
        </>
      ),
    },
    OVER_SIXTY_FIVE_CONTACT_CENTER: {
      note: (
        <>
          This is a premium projection based on your offer of cover and any terms accepted. It's
          current as of today’s date and assumes that you purchased cover today. Premium rates and
          policy fees aren't guaranteed and may change from time to time. Any change will apply from
          the next Policy Anniversary and be advised in the annual notice at least 30 days before
          the new premium rates and/or policy fees apply.
          <br />
          <br />
          Premium will increase on each Policy Anniversary based on your increase in age.
          <br />
          <br />
          You must make your own decision as to whether the cover and amounts are right for you.
        </>
      ),
    },
  };
  return { disclaimerInfo };
};

export const aboutYouDisclosureDisclaimer = {
  CC_NOTIFICATION_TEXT:
    '<Instruction: If applicant needs a reminder of their duty  during the about you entry stage, assist with the following reminder>',
  DISCLAIMER_TEXT: (
    <>
      It's important you answer our questions honestly, accurately and to the best of your
      knowledge. In answering our questions, remember{' '}
      <Link href={URLS.YOUR_DUTY_EXCLUSION_PRIVACY_NOTICE_URL} target="_blank">
        your duty
      </Link>{' '}
      to take reasonable care not to make a misrepresentation. Your answers are vital to our
      decision whether to insure you and on what terms. It may have an impact on a claim in future
      if there's misrepresentation.
    </>
  ),
  DISCLAIMER_TEXT_CC: (
    <>
      It's important you answer our questions honestly, accurately and to the best of your
      knowledge. In answering our questions, remember{' '}
      <Link href={URLS.YOUR_DUTY_EXCLUSION_PRIVACY_NOTICE_URL} target="_blank">
        your duty
      </Link>{' '}
      to take reasonable care not to make a misrepresentation. When answering our questions please
      think carefully and take the time you need. Your answers are vital to our decision whether to
      insure you and on what terms. If you make a misrepresentation, this may have an impact on a
      claim.
    </>
  ),
  DISCLAIMER_TEXT_UNSURE_CC:
    'If you are unsure about whether you should include information, or if you have difficulty answering our questions please let me know.',
  DISCLAIMER_TEXT_UNSURE: (
    <>
      If you're unsure about whether you should include information, or if you have difficulty
      answering our questions, contact us on{' '}
      <Link href={URLS.CONTACT_US_PHONE_URL}>{URLS.CONTACT_US_PHONE_NUMBER}</Link>.
    </>
  ),
  INSTRUCTIONS_FOR_CUSTOMER_CC: {
    title: 'Instructions for Customer on how to answer questions.',
    note: (
      <>
        During the application process we request that you wait until we have asked each question in
        full. We will only require Yes or No responses unless otherwise specified, <br /> for
        example a numerical value.
        <br />
        <br />
        If you answer Yes and disclose any medical conditions, we will at that time prompt you for
        further information or specifics about the disclosed condition. These follow up questions
        will also only require Yes or No responses unless specifically prompted for further detail.
      </>
    ),
    subNote: (
      <>
        Can I confirm you understand the process and are happy to commence <br /> with the
        application?
      </>
    ),
  },
  DISCLAIMER_TEXT_SELF:
    'Are you completing this application on your own behalf for your individual cover only?',
};

export const reviewApplicationDisclaimer = () => {
  const { pdsUrl } = useSharedData();
  return {
    REVIEW_ANSWER:
      'Thank you for answering our questions. Take time to review your answers below and if you need to make any changes, click the "edit" icon.',
    REVIEW_ANSWER_CC:
      'Thank you for answering our questions. I will now quickly run through the information you have provided. Please advise me immediately if there are any errors so I can update the information accordingly.',

    DUTY_CARE: (
      <>
        It's important you answer our questions honestly, accurately and to the best of your
        knowledge. In answering our questions, remember{' '}
        <Link href={URLS.YOUR_DUTY_EXCLUSION_PRIVACY_NOTICE_URL} target="_blank">
          your duty
        </Link>{' '}
        to take reasonable care not to make a misrepresentation. Your answers are vital to our
        decision whether to insure you and on what terms. It may have an impact on a claim in future
        if there's misrepresentation.
      </>
    ),

    DUTY_CARE_CC: (
      <>
        Please remember{' '}
        <Link href={URLS.YOUR_DUTY_EXCLUSION_PRIVACY_NOTICE_URL} target="_blank">
          your duty
        </Link>{' '}
        to take reasonable care not to make a misrepresentation. It is important that you ensure all
        details are accurate and not misleading. Your answers are vital to our decision whether to
        insure you and on what terms. If you make a misrepresentation, this may have an impact on a
        claim.
      </>
    ),

    UPDATE_INFO:
      'You must update any information which has changed since starting the application, up until purchase. Note that after we’ve provided you with an outcome you will no longer be able to edit your answers, and if your circumstances have changed this will require you to start a new quote.',
    UPDATE_INFO_CC:
      'You must update any information which has changed since starting the application, up until purchase. Please note that after we have provided you with an outcome you will no longer be able to edit your answers, and if your circumstances have changed this will require you to start a new quote.',

    RECORD_OF_ANSWERS:
      'Your answers to our questions will be again available on the next page. If you purchase this insurance they will be provided as part of your Welcome Pack.',
    RECORD_OF_ANSWERS_CC:
      'The answers to our questions will be available to you if you retrieve this quote. If you purchase this insurance they will be provided as part of your Welcome Pack.',

    PDS: (
      <>
        <Link href={pdsUrl} target="_blank">
          View the PDS for more details
        </Link>
      </>
    ),
  };
};

export const paymentContactDetailsDisclaimers = ({ isOnline = true }) => {
  const online = [
    {
      EMAIL_INFORMATION: {
        note: (
          <>
            All information, documents, and notices regarding your insurance will be sent
            electronically so you must let us know straightaway if your contact details change. You
            can update your preferences online or call us on{' '}
            <Link href={URLS.CONTACT_US_PHONE_URL}>{URLS.CONTACT_US_PHONE_NUMBER}</Link>.
          </>
        ),
      },
    },
  ];
  const contactCenter = [
    {
      EMAIL_INFORMATION: {
        note: `The policy contact email address will be used to send all information, documents and notices regarding your insurance electronically. You must let us know straightaway if the policy contact details change or if you want to update your delivery preference.`,
      },
    },
  ];

  const emailInfo = isOnline ? online : contactCenter;
  return { emailInfo };
};

//----------------------------------------------------------------
//** Payment Details Page */

function addNumberSuffix(number) {
  if (number >= 11 && number <= 13) {
    return number + 'th';
  }

  switch (number % 10) {
    case 1:
      return number + 'st';
    case 2:
      return number + 'nd';
    case 3:
      return number + 'rd';
    default:
      return number + 'th';
  }
}

function getTodayDateInfo() {
  const today = new Date();
  const dayNumber = addNumberSuffix(today.getDate());
  const monthNames = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ];
  const monthName = monthNames[today.getMonth()];

  return {
    dayNumber,
    monthName,
  };
}

const premiumAmountNote = ({ frequency, paymentAmount, paymentMethod }) => {
  const dateDescription = getTodayDateInfo();
  const { dayNumber, monthName } = { ...dateDescription };

  const subsequentInstalments =
    frequency === 'monthly'
      ? `${dayNumber} of each month`
      : `${dayNumber} of ${monthName} annually`;

  const bankStatementFrequency = frequency === 'monthly' ? 'month' : 'year';

  const premiumAmountNoteInfo = [
    {
      TITLE: `Your ${frequency} premium of ${paymentAmount} `,
      MESSAGE: `will be debited from your ${paymentMethod} account. Your first instalment will be deducted immediately. Subsequent instalments will be deducted on the ${subsequentInstalments}. Keep funds available for several days to allow processing time. We recommend that you check your bank statements each ${bankStatementFrequency} to ensure your cover remains up to date.`,
    },
  ];
  return premiumAmountNoteInfo;
};

const importantNoticeNote = ({ frequency }) => {
  const importantNoteInfo = [
    {
      TITLE: `Important Note`,
      MESSAGE: `To maintain your policy, you need to pay your ${frequency} premiums by the due date. We'll notify you if a payment fails and provide details on how you can make payment. If you can’t make a payment, you should get in touch with us immediately to discuss options that may be available. Your policy will be cancelled if you don't pay your overdue premiums by the date advised on your notice and you'll no longer be covered from that time.`,
    },
  ];
  return importantNoteInfo;
};

export const paymentDetailsDisclaimers = ({
  frequency,
  paymentAmount,
  paymentMethod,
  isOnline = true,
}) => {
  const online = [
    {
      PAYMENT_AMOUNT: {
        note: premiumAmountNote({
          frequency: frequency,
          paymentAmount: paymentAmount,
          paymentMethod: paymentMethod,
        }),
      },
      IMPORTANT_NOTICE: {
        note: importantNoticeNote({ frequency: frequency }),
      },
    },
  ];
  const contactCenter = [
    {
      PAYMENT_AMOUNT: {
        note: premiumAmountNote({
          frequency: frequency,
          paymentAmount: paymentAmount,
          paymentMethod: paymentMethod,
        }),
      },
      IMPORTANT_NOTICE: {
        note: importantNoticeNote({ frequency: frequency }),
      },
    },
  ];

  const paymentDetailInfo = isOnline ? online : contactCenter;
  return { paymentDetailInfo };
};

export const retrievePageDisclaimer = {
  DUTY_DISCLOSURE: (
    <>
      <Link href={URLS.YOUR_DUTY_EXCLUSION_PRIVACY_NOTICE_URL} target="_blank">
        Your Duty
      </Link>{' '}
      to take reasonable care not to make a misrepresentation still applies until you purchase a
      policy. When re-accessing this application you must ensure the answers you provided are
      accurate and not misleading, and you must update any information which has changed since
      starting your application.
    </>
  ),
};

export const onlineRetrievePageDisclaimer = {
  DUTY_DISCLOSURE: (
    <>
      <Link href={URLS.YOUR_DUTY_EXCLUSION_PRIVACY_NOTICE_URL} target="_blank">
        Your Duty
      </Link>{' '}
      to take reasonable care not to make a misrepresentation still applies until you purchase a
      policy. When re-accessing this application you must ensure the answers you provided are
      accurate and not misleading, and you must update any information which has changed since
      starting your application.
    </>
  ),
};

export const policyOutcomeDisclaimers = ({ isOnline = true }) => {
  const { pdsUrl, retrievedCoverDetails } = useSharedData();

  const online = [
    {
      UPSELL_NOTIFICATION: {
        note: (
          <>
            Critical Illness cover can assist you and your family financially if you were to suffer
            one of the defined critical illnesses.
            <br />
            <br />
            Optional Covers: Payment of a benefit under one type of cover reduces the Cover Amounts
            of all remaining benefits. In certain circumstances this may mean the end of a cover or
            the insured’s policy. Read the{' '}
            <Link href={pdsUrl} target="_blank">
              Product Disclosure Statement
            </Link>{' '}
            ‘Taking a combination of cover’ and ‘Maximum benefits payable’ sections for more details
            on limits on Cover Amounts.
          </>
        ),
      },
      TERMS_CONDITION_NO_EXCLUSION: {
        note: (
          <>
            We've relied upon the information you’ve provided to us in this application, further to{' '}
            <Link target="_blank" href={URLS.YOUR_DUTY_EXCLUSION_PRIVACY_NOTICE_URL}>
              your duty
            </Link>
            , in reaching the above outcome. For your convenience, you can access a{' '}
            <RecordOfAnswers>copy of your answers to our questions</RecordOfAnswers>
            . If you purchase this insurance, your Record of Answers will be provided as part of
            your Welcome Pack.
            <br />
            <br />
            If you’ve any questions relating to the application, the decision we have provided, or
            how to seek further information, call us on{' '}
            <Link href={URLS.CONTACT_US_PHONE_URL}>{URLS.CONTACT_US_PHONE_NUMBER}</Link>.
          </>
        ),
      },
      TERMS_CONDITION_WITH_EXCLUSION: {
        note: (
          <>
            These Special Terms and Conditions apply up to the Policy Expiry Date on the Policy
            Schedule should you purchase this policy. However, should your health, occupation, or
            lifestyle circumstances change in future, you can ask us to consider removing or
            amending any Special Terms and Conditions by contacting us on{' '}
            <Link href={URLS.CONTACT_US_PHONE_URL}>{URLS.CONTACT_US_PHONE_NUMBER}</Link> and we'll
            review your request based on the information about your circumstances at that time.
            <br />
            <br />
            We've relied upon the information you’ve provided to us in this application, further to{' '}
            <Link href={URLS.YOUR_DUTY_EXCLUSION_PRIVACY_NOTICE_URL} target="_blank">
              your duty
            </Link>
            , in reaching the above outcome. For your convenience, you can access a{' '}
            <RecordOfAnswers>copy of your answers to our questions</RecordOfAnswers>
            . If you purchase this insurance, your Record of Answers will be provided as part of
            your Welcome Pack.
            <br />
            <br />
            If you’ve any questions relating to the application, the decision we have provided, or
            how to seek further information, call us on{' '}
            <Link href={URLS.CONTACT_US_PHONE_URL}>{URLS.CONTACT_US_PHONE_NUMBER}</Link>.
          </>
        ),
      },
      TERMS_AND_CONDITIONS: {
        note: 'I understand and accept this offer of insurance including these special terms and conditions',
      },
      POLICY_OUTCOME_RETRIEVAL: (
        <>
          Welcome back. The underwriting process for this application has been completed and the
          outcome has been advised. Ensure that all information you previously provided is accurate,
          not misleading, and current as of today’s date.{' '}
          <Link href={URLS.YOUR_DUTY_EXCLUSION_PRIVACY_NOTICE_URL} target="_blank">
            Your duty
          </Link>{' '}
          to take reasonable care not to make a misrepresentation continues until you've purchased a
          policy. You must update any information which has changed since starting your application.
          <br />
          <br />
          If you need to advise us of any changes to the answers to the questions we've asked,
          contact us on <Link href={URLS.CONTACT_US_PHONE_URL}>
            {URLS.CONTACT_US_PHONE_NUMBER}
          </Link>{' '}
          to start a new application, or similarly you may also start a new application online.
          <br />
          <br />
          By proceeding with your application, you're confirming that there aren’t any changes to
          your answers to the questions we've asked. Our questions and your answers are set out in
          this application.
        </>
      ),
    },
  ];
  const contactCenter = [
    {
      UPSELL_NOTIFICATION: {
        note: (
          <>
            Critical Illness cover can assist you and your family financially if you were to suffer
            one of the defined critical illnesses.
            <br />
            <br />
            Optional Covers: Payment of a benefit under one type of cover reduces the Cover Amounts
            of all remaining benefits. In certain circumstances this may mean the end of a cover or
            the insured’s policy. Read the{' '}
            <Link target="_blank" href={pdsUrl}>
              Product Disclosure Statement
            </Link>{' '}
            ‘Taking a combination of cover’ and ‘Maximum benefits payable’ sections for more details
            on limits on Cover Amounts.
          </>
        ),
      },
      NEW_OPTIONAL_COVER_CONTACT_CENTRE: {
        note: (
          <>
            <b>Optional Covers:</b> Payment of a benefit under one type of cover reduces the Cover
            Amounts of all remaining benefits. In certain circumstances this may mean the end of a
            cover or the policy. Additionally, the Life cover Buy Back feature allows you to
            reinstate the reduced Life Cover Amount 12 months after full payment of the first
            eligible Critical Illness or Permanently Unable to Work claim. Refer to the{' '}
            <Link target="_blank" href={URLS.PDS_URL}>
              Product Disclosure Statement
            </Link>{' '}
            ‘Taking a combination of cover’ and ‘Maximum benefits payable’ sections for more details
            on limits on Cover Amounts and the ‘Life cover Buy Back’ section.
            <br /> For example:
            <br />
            <br />
            A Critical Illness payment would reduce the Cover Amounts on both the Permanently Unable
            to Work and Life benefits by the amount of the Critical Illness payment. If you had
            selected the same Cover Amount for each benefit, no cover would remain on your policy as
            a result of the Critical Illness Payment.
            <br />
            <br />
            Your policy does however include the Life cover Buy Back feature which allows you to
            reinstate the reduced Life Cover Amount 12 months after full payment of the first
            eligible Critical Illness or Permanently Unable to Work claim.
            <br />
            <br />
            You can refer to the Product Disclosure Statement for more details on limits on Cover
            Amounts.
            <br />
            <br />
            Would you like a specific example?
            <br />
            <br />
            {'<'} If Yes - Use the example from the PDS.{'>'}
          </>
        ),
      },
      NEW_NO_DECLINES_NO_SPECIAL_TERMS_CONTACT_CENTRE: {
        note: (
          <>
            We've relied upon the information you’ve provided to us in this application, further to{' '}
            <Link target="_blank" href={URLS.YOUR_DUTY_EXCLUSION_PRIVACY_NOTICE_URL}>
              your duty
            </Link>
            , in reaching this outcome. You’ve a right to request a copy of your answers to our
            questions. If you purchase this insurance, your Record of Answers will be provided as
            part of your Welcome Pack.
            <br />
            <br />
            <RecordOfAnswers>Copy of your answers to our questions</RecordOfAnswers>
          </>
        ),
      },
      TERMS_CONDITION_NO_EXCLUSION: {
        note: (
          <>
            We've relied upon the information you’ve provided to us in this application, further to{' '}
            <Link target="_blank" href={URLS.YOUR_DUTY_EXCLUSION_PRIVACY_NOTICE_URL}>
              your duty
            </Link>
            , in reaching this outcome. You’ve a right to request a copy of your answers to our
            questions. If you purchase this insurance, your Record of Answers will be provided as
            part of your Welcome Pack.
            <br />
            <br />
            <RecordOfAnswers>Copy of your answers to our questions</RecordOfAnswers>
            .
            <br />
            <br />
            If you’ve any questions relating to the application, the decision we have provided,
            please let me know.
          </>
        ),
      },
      TERMS_CONDITION_WITH_EXCLUSION: {
        note: (
          <>
            These Special Terms and Conditions apply up to the Policy Expiry Date on the Policy
            Schedule should you purchase this policy. However, should your health, occupation or
            lifestyle circumstances change in future, you can ask us to consider removing or
            amending any Special Terms and Conditions by contacting us on{' '}
            <Link href={URLS.CONTACT_US_PHONE_URL}>{URLS.CONTACT_US_PHONE_NUMBER}</Link> and we’ll
            review your request based on the information about your circumstances at that time.
            <br />
            <br />
            We've relied upon the information you’ve provided to us in this application, further to{' '}
            <Link target="_blank" href={URLS.YOUR_DUTY_EXCLUSION_PRIVACY_NOTICE_URL}>
              your duty
            </Link>
            , in reaching this outcome. You’ve a right to request a copy of your answers to our
            questions. If you purchase this insurance, your Record of Answers will be provided as
            part of your Welcome Pack.
            <br />
            <br />
            <RecordOfAnswers>Copy of your answers to our questions</RecordOfAnswers>
            .
            <br />
            <br />
            If you’ve any questions relating to the application, the decision we have provided,
            please let me know.
          </>
        ),
      },
      OUTCOME_PREMIUM_PROJECTION: {
        note: (
          <>
            Your chosen cover is not in place until the payment process has been completed. Would
            you like to go ahead and purchase this insurance now?
            <br />
            <br />
            <b>If no -</b>
            <br />
            <br />I will save and email a copy of this outcome to you for your reference, you will
            have access to this quote for 30 days.
            <br />
            <br />
            Please feel free to contact us if you have any questions or would like to make any
            changes. Would you like me to organise a follow call regarding your application?
            <br />
            <br />
            {'<'}Read the save quote text / follow end of call processes{'>'}
            <br />
            <br />
            <b>If yes -</b>
            <br />
            <br />
            Before I confirm your payment details and contact information, I need to advise you of
            some additional information about your cover.
            <br />
            <br />
            <b>Stepped Premiums and Indexation</b>
            <br />
            <br />
            Allianz Life Premiums are stepped. This means your premium increases each year based on
            your age. Increases in premiums rates may occur across groups of policyholders due to
            claims experience and other costs.
            <br />
            <br />
            Your premium will also increase if your cover amount increases, for example due to
            inflation proofing. If you don’t wish to have inflation proofing applied to your policy
            you can advise us to have it removed in a given year. There is more information about
            premium calculation and inflation proofing in the Product Disclosure Statement.
            <br />
            <br />
            <li>
              {' '}
              Would you like me to go through more detail on how inflation proofing increases work?{' '}
              {'<'}Read out the ‘Inflation proofing’ section of the PDS{'>'}
            </li>
            <br />
            <br />
            <b>Outcome Premium projection</b>
            <br />
            <br />
            <li>
              {' '}
              Would you like me to provide you with an indication of future premiums?
              <br />
              {'<'}Open Projection and read disclaimer{'>'}
            </li>
          </>
        ),
      },
      OUTCOME_CROSS_SELL: {
        note: (
          <>
            Based on your application, you’ve been pre-approved for the Critical Illness cover
            option, meaning you can choose to include this cover (up to the Life Cover Amount)
            without answering any additional questions.
            <br />
            <br />
            Is this something you would like me to provide a quote for before we finalise your
            application?
            <br />
            <br />
            {'<'}Instruction if adding CI and not already been provided with the information:
            Present the Critical Illness information provided in the CI tool tip{'>'}
          </>
        ),
      },
      TERMS_AND_CONDITIONS: {
        note: 'Do you understand and accept these special terms and conditions?',
      },
      POLICY_OUTCOME_RETRIEVAL: (email, mobile, dob = null) => (
        <>
          <Typography variant="p2-bold">
            {`<Please confirm one of the following Contact details for privacy check:>`}
          </Typography>
          <ul>
            <li>{email}</li>
            <li>{mobile}</li>
            {dob && <li>{formatDate(dob)}</li>}
            {retrievedCoverDetails[0] && <li>{retrievedCoverDetails[0]}</li>}
            {retrievedCoverDetails[1] && <li>{retrievedCoverDetails[1]}</li>}
            {retrievedCoverDetails[2] && <li>{retrievedCoverDetails[2]}</li>}
          </ul>
          The underwriting process for this application has been completed and the outcome has been
          advised. Ensure that all information you previously provided is accurate, not misleading
          and current as of today’s date.{' '}
          <Link href={URLS.YOUR_DUTY_EXCLUSION_PRIVACY_NOTICE_URL} target="_blank">
            Your duty
          </Link>{' '}
          to take reasonable care not to make a misrepresentation continues until you've purchased a
          policy. You must update any information which has changed since starting your application.
          <br />
          <br />
          <Typography variant="p2-bold">{`<If updates required>`}</Typography> As we have provided
          you with an outcome, to make any updates we will need to start a new application. Did you
          want to proceed?
          <br />
          <br />
          <Typography variant="p2-bold">
            {`<Please discuss the following with the applicant:>`}
          </Typography>
          <ul>
            <li>
              Do you understand your responsibility to provide accurate, not misleading and current
              information?
            </li>
            <li>Are there any responses that you want to review and update?</li>
            <li>
              I can assist with confirming any of your previously provided responses. Did you want
              to go through your answers now?
            </li>
            <li> Are you happy to proceed with your purchase today?</li>
          </ul>
        </>
      ),
      DECLINED_APPLICANT_PRIVACY_CHECK: (email, mobile, dob = null) => (
        <>
          <Typography variant="p2-bold">
            {`<Please confirm one of the following Contact details for privacy check:>`}
          </Typography>
          <ul>
            <li>{email}</li>
            <li>{mobile}</li>
            {dob && <li>{formatDate(dob)}</li>}
          </ul>
        </>
      ),
    },
  ];

  const policyOutcomeInfo = isOnline ? online : contactCenter;
  return { policyOutcomeInfo };
};

export const retrieveDeclineDisclaimer = {
  UNABLE_TO_COVER:
    "Unfortunately, based on the information you've disclosed cover is unable to be provided for the following reason(s):",
  OTHER_MEDICAL_INFO: 'Other medical condition information',
  DECLINE_DUTY_DISCLAIMER: (
    <>
      We've relied upon the information you've provided to us in your application, further to{' '}
      <Link href={URLS.YOUR_DUTY_EXCLUSION_PRIVACY_NOTICE_URL} target="_blank">
        your duty
      </Link>
      , in reaching the above outcome. You’ve a right to request a copy of your answers to our
      questions or have us review the outcome. For your convenience, you can access a{' '}
      <RecordOfAnswers>copy of your answers to our questions</RecordOfAnswers>. If any of this
      information is incorrect, out of date, or you’ve additional information you can provide as
      part of our review, contact us on{' '}
      <Link href={URLS.CONTACT_US_PHONE_URL}>{URLS.CONTACT_US_PHONE_NUMBER}</Link> and we'll let you
      know whether it has any impact on the outcome.
    </>
  ),
  MEDICAL_DISCLAIMER: (
    <>
      This is a simplified product and doesn't require a medical or blood test to apply. You may be
      eligible for cover elsewhere. If you’ve any questions relating to the application, the
      decision we have provided, how to seek further information, or about our
      <Link href={URLS.COMPLAINTS_PROCESS_URL} target="_blank">
        complaints process
      </Link>
      , call us on <Link href={URLS.CONTACT_US_PHONE_URL}>{URLS.CONTACT_US_PHONE_NUMBER}</Link>.
    </>
  ),
  UNABLE_TO_ASSIST:
    "We're sorry we couldn’t assist you at this time. Should your health, occupation, or lifestyle circumstances change in future, we welcome you to re-apply by contacting us.",
  UNDERWRITER_DISCLAIMER: (
    <>
      Thank you for providing additional details of your other medical condition which will help in
      the future improvement of our questions. As this is a simplified application process, we
      aren’t able to offer cover and not able to further consider your other medical condition
      without discussing the condition with our team. Any review through our team will be considered
      a new application, may require additional information from you, and doesn't guarantee that
      we'll be able to offer cover. If you'd like to discuss this further, call us on{' '}
      <Link href={URLS.CONTACT_US_PHONE_URL}>{URLS.CONTACT_US_PHONE_NUMBER}</Link> and reference
      your application number.
    </>
  ),
};

// export const policyDeclineDisclaimer = {
//   PLEASE_CONTACT_US: (
//     <>
//       Please contact us on{' '}
//       <Link href={URLS.CONTACT_US_PHONE_URL}>{URLS.CONTACT_US_PHONE_NUMBER}</Link> to continue with
//       your application.
//     </>
//   ),
//   MEDICAL_INFO_REASONS: `The medical conditions which you have disclosed require review by our Underwriting team before confirming whether we can cover you and if so, on what terms. Based on the information you've disclosed we are not at this time able to provide cover via our online service for the following reason(s):`,
//   CONTINUE_APPLICATION: (
//     <>
//       To continue with your application, we encourage you to contact us on{' '}
//       <Link href={URLS.CONTACT_US_PHONE_URL}>{URLS.CONTACT_US_PHONE_NUMBER}</Link> to discuss the
//       above disclosures.
//     </>
//   ),
//   DECLINE_DUTY_DISCLAIMER_PART_ONE: (
//     <>
//       We’ve relied upon the information you provided to us in your application, further to{' '}
//       <Link href={URLS.YOUR_DUTY_EXCLUSION_PRIVACY_NOTICE_URL} target="_blank">
//         your duty.
//       </Link>
//       Any review through our team may require a new application and/or additional information from
//       you and doesn't guarantee that we'll be able to offer cover.
//     </>
//   ),
//   DECLINE_DUTY_DISCLAIMER_PART_TWO: (
//     <>
//       You have a right to request a copy of your answers to our questions or have us review the
//       outcome. For your convenience, you can access a{' '}
//       <RecordOfAnswers>copy of your answers to our questions</RecordOfAnswers>. If any of this
//       information is incorrect, out of date, or if you have any additional information you can
//       provide as part of our review, contact us on{' '}
//       <Link href={URLS.CONTACT_US_PHONE_URL}>{URLS.CONTACT_US_PHONE_NUMBER}</Link> and we'll let you
//       know whether it has any impact on the outcome.
//     </>
//   ),
//   MEDICAL_DISCLAIMER: (
//     <>
//       This is a simplified product and doesn't require a medical or blood test to apply. You may be
//       eligible for cover elsewhere. If you have any questions relating to the application, the
//       decision we have provided, how to seek further information, or about our{' '}
//       <Link href={URLS.COMPLAINTS_PROCESS_URL} target="_blank">
//         complaints process
//       </Link>
//       , call us on <Link href={URLS.CONTACT_US_PHONE_URL}>{URLS.CONTACT_US_PHONE_NUMBER}</Link>.
//     </>
//   ),
//   UNABLE_TO_ASSIST: "We're sorry we couldn’t assist you at this time.",
//   HISTORY_MEDICAL_INFO: 'History of other medical condition information',
//   UNDERWRITER_DISCLAIMER: (
//     <>
//       Thank you for providing additional details of your other medical condition which will help in
//       the future improvement of our questions. As this is a simplified application process, we can’t
//       offer cover and are not able to further consider your other medical condition{' '}
//       <span style="font-weight:bold">without discussing this with our team.</span> Any review
//       through our team will be considered a new application, may require additional information from
//       you, and doesn't guarantee that we'll be able to offer cover. If you'd like to discuss this
//       further, , call us on{' '}
//       <Link href={URLS.CONTACT_US_PHONE_URL}>{URLS.CONTACT_US_PHONE_NUMBER}</Link> and reference
//       your application number.
//     </>
//   ),
// };

export const retrieveQuoteNotification = () => {
  const { retrievedCoverDetails } = useSharedData();
  return {
    DISCLAIMER_TEXT_REVIEW: (
      <>
        Welcome back.
        <br />
        <br />
        It's important you answer our questions honestly, accurately and to the best of your
        knowledge. In answering our questions, remember your duty to take reasonable care not to
        make a misrepresentation.
        <br />
        <br />
        Ensure that all information you previously provided is accurate, not misleading, and current
        as of today’s date.{' '}
        <Link href={URLS.YOUR_DUTY_EXCLUSION_PRIVACY_NOTICE_URL} target="_blank">
          Your duty
        </Link>{' '}
        to take reasonable care not to make a misrepresentation continues until you've purchased a
        policy. You must update any information which has changed since starting your application.
        If you're unsure about whether you should include information, have difficulty answering our
        questions or have any questions, contact us on{' '}
        <Link href={URLS.CONTACT_US_PHONE_URL}>{URLS.CONTACT_US_PHONE_NUMBER}</Link>
        . Your answers are vital to our decision whether to insure you and on what terms. It may
        have an impact on a claim in future if there's misrepresentation.
        <br />
        <br />
        By proceeding with your application, you understand your responsibility to provide accurate
        and current information and update any responses that are no longer current. Our questions
        and your answers are set out in this application.
        <br />
        <br />
        <b>Your duty to take reasonable care not to make a misrepresentation (your Duty)</b>
        <br />
        <br />
        You must take reasonable care not to make a misrepresentation to us. This responsibility
        applies until we issue you with a policy for the first time, or agree to certain
        variations/changes, or agree to reinstate your policy.
        <br />
        <br />
        You must answer our questions honestly, accurately and to the best of your knowledge. The
        answers that you give us are vital to our decision whether to insure you and on what terms.
        A misrepresentation includes a statement that's false, partially false, or which doesn’t
        fairly reflect the truth. It's not misrepresentation if you don't answer a question or if
        your answer is obviously incomplete or irrelevant to the question asked.
        <br />
        <br />
        The responsibility to take reasonable care not to make a misrepresentation applies to the
        Life Insured and the Policy Owners. If you're answering questions on behalf of anyone, we'll
        treat your answers or representations as theirs.
        <br />
        <br />
        Whether or not you've taken reasonable care not to make a misrepresentation is to be
        determined having regard to all relevant circumstances, including the type of insurance, who
        it's intended to be sold to, your particular characteristics and circumstances we are aware
        of.
        <br />
        <br />
        <b>If you don't meet your duty</b>
        <br />
        <br />
        If you don't meet the above duty, we may reject or not fully pay your claim.
        <br />
        <br />
        Within the first three years, we may treat your cover as if it never existed if we wouldn't
        have insured you on any terms if you had met the duty.
        <br />
        <br />
        Instead of treating your cover as if it never existed, we may reduce the Cover Amount using
        a formula prescribed by law (we can only do this within the first three years for Life
        cover). For Critical Illness cover and Permanently Unable to Work cover, we may also change
        the terms of your cover to put us in the same position we would have been in if you'd met
        the duty.
        <br />
        <br />
        If you deliberately provide dishonest or inaccurate answers or were reckless in answering
        our questions, this is an act of fraud, and we may treat your cover as if it never existed.
        <br />
        <br />
        We'll apply these rights separately to Life cover, Critical Illness cover, and Permanently
        Unable to Work cover.
        <br />
        <br />
        <b>Contact Us</b>
        <br />
        <br />
        If you find our information or questions are unclear, you can contact us on{' '}
        <Link href={URLS.CONTACT_US_PHONE_URL}>{URLS.CONTACT_US_PHONE_NUMBER}</Link> or{' '}
        <Link href={URLS.MORE_INFO_URL} target="_blank">
          visit our website for more information about this Duty
        </Link>
        .
      </>
    ),
    DISCLAIMER_TEXT_REVIEW_CC: (email, mobile, dob = null) => (
      <>
        <b>
          {'<'}Please confirm one of the following Contact details for privacy check:{'>'}
        </b>
        <ul>
          <li>{email}</li>
          <li>{mobile}</li>
          {dob && <li>{formatDate(dob)}</li>}
          {retrievedCoverDetails[0] && <li>{retrievedCoverDetails[0]}</li>}
          {retrievedCoverDetails[1] && <li>{retrievedCoverDetails[1]}</li>}
          {retrievedCoverDetails[2] && <li>{retrievedCoverDetails[2]}</li>}
        </ul>
        It's important you answer our questions honestly, accurately and to the best of your
        knowledge. In answering our questions, remember{' '}
        <Link href={URLS.YOUR_DUTY_EXCLUSION_PRIVACY_NOTICE_URL} target="_blank">
          your duty
        </Link>{' '}
        to take reasonable care not to make a misrepresentation.
        <br />
        <br />I will now play you some information about your duty when answering questions and how
        we collect your personal information, is that ok?
        <br />
        <br />
        <b>
          {'<'}Instruction: Play Duty recording{'>'}
        </b>
        <br />
        <br />
        Please ensure that all information you previously provided is accurate, not misleading and
        current as of today’s date.{' '}
        <Link href={URLS.YOUR_DUTY_EXCLUSION_PRIVACY_NOTICE_URL} target="_blank">
          Your duty
        </Link>{' '}
        to take reasonable care not to make a misrepresentation continues until you’ve purchased a
        policy. You must update any information which has changed since starting your application.
        If you are unsure about whether you should include information, or if you have difficulty
        answering our questions please let me know. Your answers are vital to our decision whether
        to insure you and on what terms. It may have an impact on a claim in future if there's
        misrepresentation.
        <br />
        <br />
        <b>
          {'<'}Please discuss the following with the applicant:{'>'}
        </b>
        <br />
        <ul>
          <li>
            Do you understand your responsibility to provide accurate, not misleading and current
            information?
          </li>
          <li>Are there any responses that you want to review and update?</li>
          <li>
            I can assist with confirming any of your previously provided responses. Did you want to
            go through your answers now?
          </li>
          <li>Are you happy to proceed with the quote?</li>
        </ul>
      </>
    ),
    DISCLAIMER_TEXT_REVIEW_ONLINE: (
      <>
        Welcome back.
        <br />
        <br />
        It's important you answer our questions honestly, accurately and to the best of your
        knowledge. In answering our questions, remember your duty to take reasonable care not to
        make a misrepresentation. <br />
        <br />
        Ensure that all information you previously provided is accurate, not misleading, and current
        as of today's date.{' '}
        <Link href={URLS.YOUR_DUTY_EXCLUSION_PRIVACY_NOTICE_URL} target="_blank">
          Your duty
        </Link>{' '}
        to take reasonable care not to make a misrepresentation continues until you've purchased a
        policy. You must update any information which has changed since starting your application.
        If you're unsure about whether you should include information, have difficulty answering our
        questions or have any questions, contact us on{' '}
        <Link href={URLS.CONTACT_US_PHONE_URL}>{URLS.CONTACT_US_PHONE_NUMBER}</Link> Your answers
        are vital to our decision whether to insure you and on what terms. It may have an impact on
        a claim in future if there's misrepresentation.
        <br />
        <br />
        By proceeding with your application, you understand your responsibility to provide accurate
        and current information and update any responses that are no longer current. Our questions
        and your answers are set out in this application.
        <br />
        <br />
        <b>Your duty to take reasonable care not to make a misrepresentation (your Duty)</b>
        <br />
        <br />
        You must take reasonable care not to make a misrepresentation to us. This responsibility
        applies until we issue you with a policy for the first time, or agree to certain
        variations/changes, or agree to reinstate your policy.
        <br />
        <br />
        You must answer our questions honestly, accurately and to the best of your knowledge. The
        answers that you give us are vital to our decision whether to insure you and on what terms.
        A misrepresentation includes a statement that's false, partially false, or which doesn't
        fairly reflect the truth. It's not misrepresentation if you don't answer a question or if
        your answer is obviously incomplete or irrelevant to the question asked.
        <br />
        <br />
        The responsibility to take reasonable care not to make a misrepresentation applies to the
        Life Insured and the Policy Owners. If you're answering questions on behalf of anyone, we'll
        treat your answers or representations as theirs.
        <br />
        <br />
        Whether or not you've taken reasonable care not to make a misrepresentation is to be
        determined having regard to all relevant circumstances, including the type of insurance, who
        it's intended to be sold to, your particular characteristics and circumstances we are aware
        of.
        <br />
        <br />
        <b>If you don't meet your duty</b>
        <br />
        <br />
        If you don't meet the above duty, we may reject or not fully pay your claim.
        <br />
        <br />
        Within the first three years, we may treat your cover as if it never existed if we wouldn't
        have insured you on any terms if you had met the duty.
        <br />
        <br />
        Instead of treating your cover as if it never existed, we may reduce the Cover Amount using
        a formula prescribed by law (we can only do this within the first three years for Life
        cover). For Critical Illness cover and Permanently Unable to Work cover, we may also change
        the terms of your cover to put us in the same position we would have been in if you'd met
        the duty.
        <br />
        <br />
        If you deliberately provide dishonest or inaccurate answers or were reckless in answering
        our questions, this is an act of fraud, and we may treat your cover as if it never existed.
        <br />
        <br />
        We'll apply these rights separately to Life cover, Critical Illness cover, and Permanently
        Unable to Work cover.
        <br />
        <br />
        <b>Contact Us</b>
        <br />
        <br />
        If you find our information or questions are unclear, you can contact us on{' '}
        <Link href={URLS.CONTACT_US_PHONE_URL}>{URLS.CONTACT_US_PHONE_NUMBER}</Link> or
        <Link href={URLS.YOUR_DUTY_EXCLUSION_PRIVACY_NOTICE_URL} target="_blank">
          visit our website for more information about this Duty
        </Link>
        .
      </>
    ),
    DISCLAIMER_TEXT_OUTCOME: (
      <>
        Welcome back. The underwriting process for this application has been completed and the
        outcome has been advised. Ensure that all information you previously provided is accurate,
        not misleading, and current as of today’s date.{' '}
        <Link href={URLS.YOUR_DUTY_EXCLUSION_PRIVACY_NOTICE_URL} target="_blank">
          Your duty
        </Link>{' '}
        to take reasonable care not to make a misrepresentation continues until you've purchased a
        policy. You must update any information which has changed since starting your application.
        <br />
        <br />
        If you need to advise us of any changes to the answers to the questions we've asked, contact
        us on <Link href={URLS.CONTACT_US_PHONE_URL}>{URLS.CONTACT_US_PHONE_NUMBER}</Link> to start
        a new application, or similarly you may also start a new application online.
        <br />
        <br />
        By proceeding with your application, you're confirming that there aren’t any changes to your
        answers to the questions we've asked. Our questions and your answers are set out in this
        application.
      </>
    ),
    DISCLAIMER_TEXT_OUTCOME_CC: (
      <>
        The underwriting process for this application has been completed and the outcome has been
        advised. Ensure that all information you previously provided is accurate, not misleading and
        current as of today’s date.{' '}
        <Link href={URLS.YOUR_DUTY_EXCLUSION_PRIVACY_NOTICE_URL} target="_blank">
          Your duty
        </Link>{' '}
        to take reasonable care not to make a misrepresentation continues until you've purchased a
        policy. You must update any information which has changed since starting your application.
        <br />
        <br />
        <b>
          {'<'}If updates required{'>'}
        </b>{' '}
        As we have provided you with an outcome, to make any updates we will need to start a new
        application. Did you want to proceed?
        <br />
        <br />
        <b>
          {'<'}Please discuss the following with the applicant:{'>'}
        </b>{' '}
        <br />
        <ul>
          <li>
            Do you understand your responsibility to provide accurate, not misleading and current
            information?
          </li>
          <li>Are there any responses that you want to review and update?</li>
          <li>
            I can assist with confirming any of your previously provided responses. Did you want to
            go through your answers now?
          </li>
          <li>Are you happy to proceed with your purchase today?</li>
        </ul>
      </>
    ),

    NEW_SYSTEM: (
      <>
        We've recently made changes to our system. If you started an application prior to{' '}
        <b>2 December 2023</b>, we aren't able to retrieve your application. Please{' '}
        <Link href={URLS.ALLIANZ_WEBSITE_URL} target="_blank">
          start a new application
        </Link>{' '}
        or call us on <Link href={URLS.CONTACT_US_PHONE_URL}>{URLS.CONTACT_US_PHONE_NUMBER}</Link>.
      </>
    ),
    POLICY_EXPIRED: (
      <>
        Note: This application has expired and cannot be updated. For customer enquiries you can
        review the Application ROA details below.
      </>
    ),
  };
};

//----------------------------------------------------------------
export const paymentConfirmationDisclaimers = ({ isOnline = true, deliveryType = 'email' }) => {
  const { showPromo, campaignData } = useSharedData();

  const isShowGiftCardCopy =
    showPromo &&
    typeof campaignData?.additionalTnC === 'string' &&
    campaignData?.additionalTnC.length > 0;

  const online = [
    {
      WELCOME_PACK: {
        title: `Your welcome pack`,
        note: (
          <>
            We’ve completed the application process and your policy has been issued. You can
            download and print your Welcome Pack by clicking on the button below. If you've chosen
            to receive your Welcome Pack via email, it'll be sent shortly.
            <br />
            <br />
            Your Welcome Pack contains all the information that was finalised today and a Product
            Disclosure Statement and Policy Document. These documents set out in detail the cover
            you have, as well as any special terms that apply to it. In addition, a Record of
            Answers details all the information you’ve provided. Read through your paperwork to make
            sure all details are correct and that the cover is what you require. You must contact us
            immediately if any information in the Record of Answers is incorrect.
            <br />
            <br />
            You have a 30 day cooling-off period in which you may cancel the insurance by contacting
            Allianz and you'll receive a full premium refund (unless a claim has been or could be
            made on the policy). If you do cancel after the cooling-off period, we may charge you
            for the period of time that you were insured with us.
            <span style={{ display: isShowGiftCardCopy ? 'block' : 'none' }}>
              <br />
              <br />
              {campaignData?.additionalTnC}{' '}
              <LocalLink href={campaignData?.tncUrl} target="_blank" removeSpaces={true}>
                {URLS.GIFT_CARD_TERM_CONDITIONS.trim()}
              </LocalLink>
            </span>
            <br />
            <br />
            Contact us on{' '}
            <LocalLink removeSpaces={true} href={URLS.CONTACT_US_PHONE_URL}>
              {URLS.CONTACT_US_PHONE_NUMBER}
            </LocalLink>{' '}
            to make any changes or if you’ve any further enquiries or concerns in relation to your
            policy.
          </>
        ),
      },
    },
  ];
  const contactCenter = [
    {
      title: `Your welcome pack`,
      WELCOME_PACK: {
        note: (
          <>
            Thank You. We have now completed the application process and your policy has been
            issued.
            <br />
            <br />
            You will receive a Welcome pack by {deliveryType}. This contains all the information
            that was finalised today and a PDS and Policy Document. These documents set out in
            detail the cover you have as well as any special terms that apply to it. In addition, a
            Record of Answers details all the information you have provided. Please read through
            your paperwork to make sure all details are correct, and the cover is what you require.
            We would like to emphasise the importance of reviewing the answers we have recorded to
            the application questions to ensure all responses are complete and accurate. You must
            contact us immediately if any information in the Record of Answers is incorrect.
            <br />
            <br />
            You have a 30 day cooling-off period in which you may cancel the insurance by contacting
            Allianz and you will receive a full premium refund (unless a claim has been or could be
            made on the policy). If you do cancel after the cooling-off period, we may charge you
            for the period of time that you were insured with us.
            <span style={{ display: isShowGiftCardCopy ? 'block' : 'none' }}>
              <br />
              <br />
              {campaignData?.additionalTnC}{' '}
              <LocalLink href={campaignData?.tncUrl} target="_blank" removeSpaces={true}>
                {URLS.GIFT_CARD_TERM_CONDITIONS.trim()}
              </LocalLink>
            </span>
            <br />
            <br />
            Please contact us to make any changes or if you have any further enquiries or concerns
            in relation to your policy.
          </>
        ),
      },
    },
  ];

  const welcomePackInfo = isOnline ? online : contactCenter;
  return { welcomePackInfo };
};
// export const paymentConfirmationDisclaimers = ({ isOnline = true, deliveryType = 'email' }) => {
//   const online = [
//     {
//       WELCOME_PACK: {
//         title: `Your welcome pack`,
//         note: (
//           <>
//             We’ve completed the application process and your policy has been issued. You can
//             download and print your Welcome Pack by clicking on the button below. If you've chosen
//             to receive your Welcome Pack via email, it'll be sent shortly.
//             <br />
//             <br />
//             Your Welcome Pack contains all the information that was finalised today and a Product
//             Disclosure Statement and Policy Document. These documents set out in detail the cover
//             you have, as well as any special terms that apply to it. In addition, a Record of
//             Answers details all the information you’ve provided. Read through your paperwork to make
//             sure all details are correct and that the cover is what you require. You must contact us
//             immediately if any information in the Record of Answers is incorrect.
//             <br />
//             <br />
//             You have a 30 day cooling-off period in which you may cancel the insurance by contacting
//             Allianz and you'll receive a full premium refund (unless a claim has been or could be
//             made on the policy). If you do cancel after the cooling-off period, we may charge you
//             for the period of time that you were insured with us.
//             <br />
//             <br />
//             Contact us on
//             <Link href={URLS.CONTACT_US_PHONE_URL}>{URLS.CONTACT_US_PHONE_NUMBER}</Link>
//             to make any changes or if you’ve any further enquiries or concerns in relation to your
//             policy.
//           </>
//         ),
//       },
//     },
//   ];
//   const contactCenter = [
//     {
//       title: `Your welcome pack`,
//       WELCOME_PACK: {
//         note: (
//           <>
//             `Thank You. We have now completed the application process and your policy has been
//             issued.
//             <br />
//             <br />
//             You will receive a Welcome pack by {deliveryType}. This contains all the information
//             that was finalised today and a PDS and Policy Document. These documents set out in
//             detail the cover you have as well as any special terms that apply to it. In addition, a
//             Record of Answers details all the information you have provided. Please read through
//             your paperwork to make sure all details are correct, and the cover is what you require.
//             We would like to emphasise the importance of reviewing the answers we have recorded to
//             the application questions to ensure all responses are complete and accurate. You must
//             contact us immediately if any information in the Record of Answers is incorrect.
//             <br />
//             <br />
//             You have a 30 day cooling-off period in which you may cancel the insurance by contacting
//             Allianz and you will receive a full premium refund (unless a claim has been or could be
//             made on the policy). If you do cancel after the cooling-off period, we may charge you
//             for the period of time that you were insured with us.
//             <br />
//             <br />
//             Please contact us to make any changes or if you have any further enquiries or concerns
//             in relation to your policy.`
//           </>
//         ),
//       },
//     },
//   ];

//   const welcomePackInfo = isOnline ? online : contactCenter;
//   return { welcomePackInfo };
// };
//----------------------------------------------------------------

export const retrieveExpiredDisclaimer = {
  APPLICATION_EXPIRED: 'Your application has expired',
  APPLICATION_POSTPONED: 'Your application was postponed',
  EXPIRED_DISCLAIMER_CC: (
    <>
      Your application has expired and we're unable to retrieve it. Please re-apply. If you believe
      that this is an error and wish to continue your application, or would like more information,
      please let me know.
    </>
  ),
  EXPIRED_DISCLAIMER_ONLINE: (
    <>
      Your application has expired and we’re unable to retrieve it. Please re-apply. If you believe
      that this is an error and wish to continue your application, or for more information, call our
      friendly Life Insurance Specialists on
      <Link href={URLS.CONTACT_US_PHONE_URL}>{URLS.CONTACT_US_PHONE_NUMBER}</Link>{' '}
    </>
  ),
  EXPIRED_PP_DISCLAIMER_ONLINE: (
    <>
      We're unable to retrieve your application. Please re-apply.
      <br />
      If you believe this is an error and wish to continue your application, or for more
      information, call our friendly Life Insurance Specialists on
      <Link href={URLS.CONTACT_US_PHONE_URL}>{URLS.CONTACT_US_PHONE_NUMBER}</Link>
      {'. '}
      You can also start a new application if the reasons for your postponement have changed.
    </>
  ),
  EXPIRED_D_DISCLAIMER_ONLINE: (
    <>
      Based on the information you've provided, we believe that we've already assessed an
      application by you for this type of cover and we've been unable to provide cover based on:
      <ul>
        <li>occupation</li>
        <li>state of health</li>
        <li>family history, or</li>
        <li>pastimes</li>
      </ul>
      If you believe that is an error and wish to continue your application, or for more
      information, call our friendly Life Insurance Specialists on
      <Link href={URLS.CONTACT_US_PHONE_URL}>{URLS.CONTACT_US_PHONE_NUMBER}</Link>
      {'.'}
    </>
  ),
};

export const retrieveBoundDisclaimer = {
  ALREADY_PURCHASED: "You've already purchased this application",
  BOUND_DISCLAIMER: (
    <>
      All the relevant information regarding your answers to our questions, policy, and cover will
      be in the welcome pack provided when you purchased the policy.
      <br />
      <br />
      If you believe this is an error and wish to continue your application, or for more
      information, call our friendly Life Insurance Specialists on
      <Link href={URLS.CONTACT_US_PHONE_URL}>{URLS.CONTACT_US_PHONE_NUMBER}</Link>{' '}
    </>
  ),
};

export const retrieveCommonDisclaimer = {
  SORRY_DISCLAIMER: "We're sorry",
  QUOTE_REFERENCE: 'Quote reference: ',
  CLOSE: 'CLOSE',
};

const navigateToOnlineNewApplication = () => {
  const currentDomain = window.location.origin;
  window.location.href = `${currentDomain}${PATH_ONLINE.gettingStarted}`;
};

export const onlineRetrieveDisclaimer = {
  DISCLAIMER_TITLE: 'Application retrieval important information',
  DISCLAIMER: (
    <>
      We've recently made changes to our system. If you started an application prior to{' '}
      <b>2 December 2023</b>, we aren't able to retrieve your application. Please
      <Link href="javascript:;" onClick={navigateToOnlineNewApplication}>
        start a new application
      </Link>{' '}
      or call us on <Link href={URLS.CONTACT_US_PHONE_URL}>{URLS.CONTACT_US_PHONE_NUMBER}</Link>.
    </>
  ),
  RETRIEVE_MODAL_NOTIFICATION_TITLE: 'Important information',
  RETRIEVE_MODAL_NOTIFICATION: (
    <>
      If the information you've provided is incorrect, please call our friendly Life Insurance
      Specialists on <Link href={URLS.CONTACT_US_PHONE_URL}>{URLS.CONTACT_US_PHONE_NUMBER}</Link>
      to discuss your application.
    </>
  ),
  RETRIEVE_MODAL_TITLE: 'Allianz securely manages your information',
  RETRIEVE_MODAL_DESCRIPTION:
    "This mobile number is linked to your life insurance quote. To keep your information secure, we'll be using this number to send you a verification code when you proceed or retrieve your saved application.",
  RETRIEVE_MODAL_SEND_CODE: 'SEND CODE VIA SMS',
};

export const errorNotification = {
  IMPROVING_SERVICE: 'We are improving our services',
  NOT_AVAILABLE_TRY_AGAIN: (
    <>
      Allianz Life is currently unavailable.
      <br />
      Please try again later,{' '}
      <Link href={PATH_ONLINE.retrieveQuote}>retrieve your application</Link>, or call us on{' '}
      <Link href={URLS.CONTACT_US_PHONE_URL}>{URLS.CONTACT_US_PHONE_NUMBER}</Link>. We are here to
      help.
    </>
  ),
  NOT_AVAILABLE_TRY_AGAIN_MAINT: (
    <>
      Allianz Life is currently unavailable.
      <br />
      Please try again later or call us on{' '}
      <Link href={URLS.CONTACT_US_PHONE_URL}>{URLS.CONTACT_US_PHONE_NUMBER}</Link>. We are here to
      help.
    </>
  ),
  RETURN_ALLIANZ_HOMEPAGE: 'CLOSE WINDOW AND RETURN TO ALLIANZ HOMEPAGE',
};

export const alreadyDeclinedDisclaimer = {
  ALREADY_ASSESSED: (
    <>
      If you believe this is an error, the information provided is now out of date, or you have
      additional information you can provide as part of our review, call us on{' '}
      <Link href={URLS.CONTACT_US_PHONE_URL}>{URLS.CONTACT_US_PHONE_NUMBER}</Link>.
    </>
  ),
  ALREADY_ASSESSED_UPDATE: (
    <>
      Our records show that your application for Life Insurance has already been assessed. The
      responses that you provided to our questions have determined that cover cannot be offered, has
      been postponed or requires further review by our underwriting team for one or more of the
      following reasons.
    </>
  ),
  ALREADY_DECLINED_CALLBACK: (
    <>
      If you believe this is an error, the information provided is now out of date, or you have
      additional information you can provide as part of our review, call us on{' '}
      <Link href={URLS.CONTACT_US_PHONE_URL}>{URLS.CONTACT_US_PHONE_NUMBER}</Link>.
    </>
  ),
  ALREADY_DECLINED_CALLBACK_UPDATE: {
    tite: 'We can help you understand the information you previously provided.',
    note: (
      <>
        If you believe this is an error, the information provided is now out of date, or you have
        additional information you can provide as part of our review, call us on{' '}
        <Link href={URLS.CONTACT_US_PHONE_URL}>{URLS.CONTACT_US_PHONE_NUMBER}</Link> to start a new
        quote.
      </>
    ),
  },
};

export const communicationConsent = {
  CONSENT: (
    <>
      Do you consent to being contacted about this application for an Allianz life insurance policy?
      We may contact you by telephone (either by a phone call or SMS), or email. You can vary or
      withdraw your consent by contacting us on{' '}
      <LocalLink variant="p3-link-bold" href={URLS.CONSENT_PHONE_URL} sx={{ fontSize: 20 }}>
        {URLS.CONSENT_PHONE_NUMBER}
      </LocalLink>
    </>
  ),
};

export const saveScript = ({ quoteNumber = '' }) => {
  return {
    SCRIPT_REVIEW: (
      <>
        Thank you, your application was successfully saved and your application reference number is{' '}
        {quoteNumber}.
        <br />
        <br />
        Your application will be saved for 30 days. You'll need this reference number and mobile
        number to retrieve the application. ​
        <br />
        <br />
        Your duty to take reasonable care not to make a misrepresentation still applies until you
        purchase a policy. When re-accessing your application you must ensure the answers you
        provided are accurate and not misleading, and you must update any information which has
        changed since starting your application. We'll ask you to confirm if there have been any
        changes to your answers.
        <br />
        <br />
        In the meantime, if you have queries related to your application, contact us on{' '}
        <Link href={URLS.CONTACT_US_PHONE_URL}>{URLS.CONTACT_US_PHONE_NUMBER}</Link>
      </>
    ),
  };
};
